import { useCallback } from 'react';

import { type AirActionTrackingLocation, useTrackDownloadedProxyAssets } from '@air/analytics';
import { Clips } from '@air/api';
import { useToasts } from '@air/provider-toast';
import { reportErrorToBugsnag } from '@air/utils-error';

import { usePrivateAssetOpenLocation } from '~/hooks/usePrivateAssetOpenLocation';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { DownloadFile, isS3DownloadUrlActive } from '~/utils/DownloadFile';
import { getPrivateDownloadUrlOptions } from '~/utils/internalAnalytics/download';

export type DownloadPrivateProxyAssetParams = {
  id: string;
  resolutionId: string;
  trackLocation: AirActionTrackingLocation;
};

/**
 * This hook returns a function that downloads a single private proxy asset.
 */
export const useDownloadPrivateProxyAsset = () => {
  const { showToast } = useToasts();
  const { trackDownloadedProxyAssets } = useTrackDownloadedProxyAssets();
  const { currentWorkspace } = useCurrentWorkspace();
  const workspaceId = currentWorkspace?.id;
  const analyticsLocation = usePrivateAssetOpenLocation();

  const downloadPrivateProxyAsset = useCallback(
    async ({ id, resolutionId, trackLocation }: DownloadPrivateProxyAssetParams) => {
      if (!workspaceId) {
        throw new Error('No workspace id');
      }

      try {
        const { url } = await Clips.getDownloadUrl({
          clipId: id,
          workspaceId,
          options: getPrivateDownloadUrlOptions({ altResolutionId: resolutionId, analyticsLocation }),
        });

        if (isS3DownloadUrlActive(url)) {
          DownloadFile(url);

          trackDownloadedProxyAssets({
            id,
            proxyId: resolutionId,
            location: trackLocation,
          });
        } else {
          showToast('One or more of the selected file downloads has expired.');
        }
      } catch (error) {
        showToast('There was an error downloading assets.');

        reportErrorToBugsnag({
          error,
          context: 'Failed to download private assets from selection menu',
          metadata: {
            data: {
              id,
              resolutionId,
              location: trackLocation,
              analyticsLocation,
            },
          },
        });
      }
    },
    [analyticsLocation, showToast, trackDownloadedProxyAssets, workspaceId],
  );

  return {
    downloadPrivateProxyAsset,
  };
};
